/**
 * 画面下部のトースト。操作結果・エラーの通知に使う。
 *
 * Popover API(popover="manual")でブラウザの**トップレイヤー**に表示する(2026-08-24)。
 * モーダルは <dialog>(トップレイヤー)で表示しているため、通常のz-indexでは
 * トーストがモーダルの裏に隠れてエラーが見えなくなる。後から showPopover した
 * 要素はトップレイヤー内でさらに前面に積まれるため、モーダルの上に必ず見える。
 *
 * Popover API が無いブラウザ(iOS 16 以前など)への対応(2026-09-16 改修依頼 F-3):
 * 機能検出で分岐し、使えないときは従来の表示方式にする。そのままだと開いているモーダルの
 * 裏に隠れるため、表示の瞬間にいちばん手前のモーダルの中へ置いて前面に出す。見た目は同じ。
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type ToastContextValue = (message: string) => void;

const ToastContext = createContext<ToastContextValue | null>(null);

type PopoverDiv = HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void };

/** Popover API が使えるか(機能検出) */
const supportsPopover = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;

/** いちばん手前に開いているモーダル(無ければ body) */
function frontHost(): HTMLElement {
  const open = document.querySelectorAll<HTMLDialogElement>('dialog[open]');
  return open.length > 0 ? open[open.length - 1]! : document.body;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  /** Popover API が無いときの置き場所(表示のたびに、いちばん手前のモーダルを選び直す) */
  const [host, setHost] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<PopoverDiv | null>(null);

  const toast = useCallback((msg: string) => {
    if (!supportsPopover) setHost(frontHost());
    setMessage(msg);
    setVisible(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), 3200);
  }, []);

  // 表示のたびに閉じて開き直す(モーダルより後に開くことでトップレイヤーの最前面に出す)
  useEffect(() => {
    if (!supportsPopover) return;
    const el = boxRef.current;
    if (!el) return;
    try {
      if (visible) {
        el.hidePopover?.();
        el.showPopover?.();
      } else {
        el.hidePopover?.();
      }
    } catch {
      // 表示の切り替えに失敗しても、CSSのopacity切替で表示される
    }
  }, [visible, message]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const box = supportsPopover ? (
    <div ref={boxRef} popover="manual" className={`toast${visible ? ' on' : ''}`} role="status" aria-live="polite">
      {message}
    </div>
  ) : (
    <div ref={boxRef} className={`toast${visible ? ' on' : ''}`} role="status" aria-live="polite">
      {message}
    </div>
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {supportsPopover ? box : createPortal(box, host && host.isConnected ? host : document.body)}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast は ToastProvider の内側で使ってください');
  return ctx;
}
